<template>
  <div class="work-plan-container pa-4" style="background-color: #F3F4F6; min-height: 100vh;">
    <v-row>
      <!-- Main Content Left -->
      <v-col cols="12" md="9">
        <v-card class="rounded-xl pa-4 elevation-0 h-100">
          <!-- Header Actions -->
          <div class="d-flex align-center justify-space-between mb-4">
            <h2 class="text-subtitle-1 font-weight-bold text-uppercase mb-0 d-flex align-center flex-wrap">
              {{ activeTab === 0 ? 'Quản lý ca làm việc phòng IT' : 'Quản lý tăng ca OT phòng IT' }} -&nbsp;
              <v-menu v-model="monthMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                max-width="290px" min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"
                    class="teal--text text--darken-4 cursor-pointer hover-underline d-inline-flex align-center">
                    {{ selectedMonthText }}
                    <v-icon color="teal darken-4" class="ml-1">mdi-menu-down</v-icon>
                  </span>
                </template>
                <v-date-picker v-model="selectedMonth" type="month" locale="vi-vn" no-title scrollable
                  color="teal darken-4" @change="onMonthChange"></v-date-picker>
              </v-menu>
            </h2>
          </div>

          <v-tabs v-model="activeTab" background-color="transparent" color="teal darken-4" class="mb-4">
            <v-tab class="font-weight-bold">Ca làm việc</v-tab>
            <v-tab class="font-weight-bold">Tăng ca OT</v-tab>
          </v-tabs>

          <v-row class="mb-4 align-end">
            <v-col cols="12" md="3" class="py-0">
              <span class="text-caption grey--text text--darken-1 font-weight-medium">Phòng ban:</span>
              <v-select v-model="selectedBranch" :items="branches" outlined dense hide-details
                class="rounded-lg mt-1 font-weight-medium" background-color="white"></v-select>
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <span class="text-caption grey--text text--darken-1 font-weight-medium">Thời gian:</span>
              <div class="d-flex align-center mt-1 border rounded-lg px-2 bg-white"
                style="height: 40px; border: 1px solid #E0E0E0;">
                <v-icon small :color="canPrevWeek ? 'grey darken-2' : 'grey lighten-3'"
                  :class="{ 'cursor-pointer': canPrevWeek }" :disabled="!canPrevWeek"
                  @click="canPrevWeek && prevWeek()">mdi-chevron-left</v-icon>
                <span class="mx-auto font-weight-medium text-body-2">{{ dateRangeText }}</span>
                <v-icon small :color="canNextWeek ? 'grey darken-2' : 'grey lighten-3'"
                  :class="{ 'cursor-pointer': canNextWeek }" :disabled="!canNextWeek"
                  @click="canNextWeek && nextWeek()">mdi-chevron-right</v-icon>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="py-0 d-flex justify-end mt-4 mt-md-0">
              <v-btn outlined color="teal darken-2" class="rounded-lg mr-3" height="40" width="40" min-width="40"
                @click="loadData" title="Tải lại">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text" height="40">
                <v-icon left small color="white">mdi-calendar-check</v-icon> Lưu lịch đã tạo
              </v-btn>
            </v-col>
          </v-row>

          <!-- Legend -->
          <div class="d-flex align-center mb-4 text-caption font-weight-medium flex-wrap">
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-green"></div> Sáng (06:00 - 14:00)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-teal-wfh"></div> WFH (07:30 - 17:30)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-orange"></div> Chiều (14:00 - 22:00)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-blue"></div> Gãy (tùy lịch sắp xếp)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-grey"></div> OFF IT (00:00 - 00:00)
            </div>
          </div>

          <!-- Thông báo đang lọc nhân viên -->
          <div v-if="onlyShowSelected" class="d-flex align-center mb-3">
            <v-chip small color="orange lighten-5" class="orange--text text--darken-4 font-weight-bold" close
              close-icon="mdi-close" @click:close="onlyShowSelected = false" style="border: 1px solid #FFE0B2;">
              <v-icon left small color="orange darken-3">mdi-filter</v-icon>
              Đang lọc hiển thị duy nhất: {{ selectedEmployeeName || selectedEmployeeId }}
              <span class="grey--text text--darken-1 ml-2 font-weight-regular">(Bấm nút X để xem tất cả nhân
                viên)</span>
            </v-chip>
          </div>

          <!-- Schedule Table -->
          <div style="position: relative;">
            <v-progress-linear v-if="loading" indeterminate color="teal darken-4" absolute top
              style="z-index: 2;"></v-progress-linear>
            <v-simple-table class="schedule-table border" fixed-header height="550px"
              :style="{ border: '1px solid #E0E0E0', opacity: loading ? 0.6 : 1 }">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center font-weight-bold" style="width: 200px;">
                      <div class="d-flex align-center justify-center">
                        <span>Nhân sự</span>
                        <!-- Nút ẩn/hiện nhanh trên tiêu đề cột -->
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon x-small class="ml-1"
                              :color="onlyShowSelected ? 'orange darken-3' : 'grey darken-1'"
                              @click.stop="toggleShowSelected">
                              <v-icon x-small>{{ onlyShowSelected ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                              }}</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ onlyShowSelected ? 'Đang chỉ hiện NV chọn (Bấm để hiện tất cả)' : 'Bấm để chỉ hiện NV đang chọn' }}</span>
                        </v-tooltip>
                      </div>
                    </th>
                    <th class="text-center font-weight-bold" v-for="day in days" :key="day.date"
                      :class="{ 'greyed-out-header': day.isGreyedOut }">
                      {{ day.name }}<br><span class="grey--text font-weight-regular"
                        :class="{ 'greyed-out-text': day.isGreyedOut }">{{
                          day.date }}</span>
                    </th>
                    <th class="text-center font-weight-bold" style="width: 80px;">Tổng Giờ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="emp in displayedEmployees" :key="emp.id" :id="'emp-row-' + emp.id"
                    :class="{ 'highlight-selected-emp': emp.id === selectedEmployeeId }">
                    <td class="px-2 py-3 employee-cell cursor-pointer" @click="selectedEmployeeId = emp.id"
                      :title="'Chọn nhân sự ' + emp.name">
                      <div class="d-flex align-center">
                        <v-avatar size="40" class="mr-3 avatar-clickable"
                          :class="{ 'avatar-selected': emp.id === selectedEmployeeId }"
                          @click.stop="selectedEmployeeId = emp.id" title="Chọn nhanh nhân sự">
                          <img :src="emp.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold text-body-2"
                            :class="{ 'orange--text text--darken-4': emp.id === selectedEmployeeId }">
                            {{ emp.name }}
                          </div>
                          <div class="text-caption"
                            :class="emp.id === selectedEmployeeId ? 'orange--text text--darken-3 font-weight-medium' : 'grey--text'">
                            {{ emp.id }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td v-for="(shift, index) in emp.shifts" :key="index" class="text-center px-1"
                      :class="{
                        'greyed-out-cell': days[index] && days[index].isGreyedOut,
                        'cell-selected': emp.id === selectedEmployeeId && selectedDays.includes(index),
                        'cursor-pointer': !(days[index] && days[index].isGreyedOut)
                      }"
                      @click="onCellClick(emp, index)">
                      <template v-if="days[index] && days[index].isGreyedOut">
                        <div class="greyed-out-placeholder">—</div>
                      </template>
                      <template v-else>
                        <!-- Rotation Badge -->
                        <div v-if="days[index] && getRotationDetail(emp.id, days[index].fullDate)" class="mb-1">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip v-bind="attrs" v-on="on" color="indigo lighten-5"
                                class="indigo--text text--darken-3 font-weight-bold px-1 py-0" x-small
                                style="font-size: 9px !important; border: 1px solid #C5CAE9; height: 16px;">
                                <v-icon x-small class="mr-1" color="indigo darken-3">mdi-swap-horizontal</v-icon>
                                Điều chuyển
                              </v-chip>
                            </template>
                            <span>
                              <strong>Thông tin điều chuyển:</strong><br>
                              - Ngày: {{ formatRotationTooltipDate(getRotationDetail(emp.id, days[index].fullDate))
                              }}<br>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).fromDepartmentName || getRotationDetail(emp.id, days[index].fullDate).fromDepartment">
                                - Từ: {{ getRotationDetail(emp.id, days[index].fullDate).fromDepartmentName ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).fromDepartment }}<br>
                              </span>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).toDepartmentName || getRotationDetail(emp.id, days[index].fullDate).toDepartment">
                                - Đến: {{ getRotationDetail(emp.id, days[index].fullDate).toDepartmentName ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).toDepartment }}<br>
                              </span>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).notes || getRotationDetail(emp.id, days[index].fullDate).Notes">
                                - Ghi chú: {{ getRotationDetail(emp.id, days[index].fullDate).notes ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).Notes }}
                              </span>
                            </span>
                          </v-tooltip>
                        </div>

                        <template v-if="shift">
                          <div :class="['shift-block', shift.type, 'position-relative']"
                            style="cursor: pointer;" title="Bấm để chọn ca / Bấm X để xóa">
                            <v-btn icon x-small class="shift-delete-btn" @click.stop="deleteShift(emp, index)" title="Xóa ca này">
                              <v-icon x-small color="grey darken-2">mdi-close</v-icon>
                            </v-btn>
                            <div class="font-weight-bold">{{ shift.title }}</div>
                            <div class="shift-time" v-if="shift.time1">{{ shift.time1 }}</div>
                            <div class="shift-time" v-if="shift.time2">{{ shift.time2 }}</div>
                          </div>
                        </template>
                        <template v-else>
                          <div class="empty-shift-cell" title="Bấm để chọn ca">
                            <v-icon x-small class="empty-plus-icon" color="grey lighten-2">mdi-plus</v-icon>
                          </div>
                        </template>
                      </template>
                    </td>
                    <td class="text-center font-weight-bold text-body-2"
                      :class="{ 'orange--text text--darken-4': emp.id === selectedEmployeeId }">
                      {{ emp.totalHours }}h
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Debug JSON -->
          <div class="mt-6" v-show="false">
            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-2 d-flex align-center">
              <v-icon small class="mr-2" color="grey darken-2">mdi-code-json</v-icon>
              Dữ liệu JSON Debug (employees)
            </div>
            <pre class="pa-3 rounded-lg grey lighten-4 text-caption font-weight-medium overflow-x-auto"
              style="border: 1px solid #E0E0E0; max-height: 300px; font-family: monospace;">{{ employeesDebugJson }}</pre>
          </div>

        </v-card>
      </v-col>

      <!-- Right Panel: Registration -->
      <v-col cols="12" md="3">
        <v-card class="rounded-xl pa-4 elevation-0 h-100 d-flex flex-column">
          <h3 class="text-subtitle-1 font-weight-bold text-uppercase mb-4">{{ activeTab === 0 ? 'Đăng ký ca làm việc' :
            'Đăng ký tăng ca OT' }}</h3>

          <!-- Dynamic Employee Selector -->
          <v-select v-model="selectedEmployeeId" :items="employees" item-text="name" item-value="id" outlined dense
            hide-details class="rounded-lg mb-6 font-weight-medium" background-color="#F9FAFB"
            label="Chọn nhân viên đăng ký">
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="32" class="mr-2">
                  <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2" style="line-height: 1.2;">{{ item.name }}</div>
                  <div class="text-caption grey--text" style="line-height: 1.2;">{{ item.id }}</div>
                </div>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="32" class="mr-2">
                  <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2">{{ item.name }}</div>
                  <div class="text-caption grey--text">{{ item.id }}</div>
                </div>
              </div>
            </template>
          </v-select>

          <div class="mb-4" v-if="days && days.length > 0">
            <div class="d-flex justify-space-between text-caption font-weight-medium mb-2 px-1">
              <span v-for="day in days" :key="'reg-name-' + day.date"
                :class="{ 'greyed-out-text': day.isGreyedOut || (isMorningShift && !isWeekend(day)) }"
                style="width: 14%; text-align: center;">{{ day.name === 'Chủ Nhật' ? 'CN' : day.name }}</span>
            </div>
            <div class="d-flex justify-space-between px-1">
              <v-icon v-for="(day, index) in days" :key="'reg-icon-' + day.date"
                :color="day.isGreyedOut ? 'grey lighten-3' : (isMorningShift && !isWeekend(day) ? 'grey lighten-2' : (selectedDays.includes(index) ? 'teal darken-2' : 'grey lighten-1'))"
                small style="width: 14%; text-align: center;"
                :class="{ 'cursor-pointer': !(day.isGreyedOut || (isMorningShift && !isWeekend(day))), 'cursor-not-allowed': isMorningShift && !isWeekend(day) }"
                :disabled="day.isGreyedOut"
                :title="isMorningShift && !isWeekend(day) ? 'Ca sáng chỉ áp dụng cho Thứ 7 hoặc Chủ Nhật' : ''"
                @click="toggleRegDay(index)">
                {{ day.isGreyedOut ? 'mdi-minus-box-outline' : (isMorningShift && !isWeekend(day) ? 'mdi-minus-box-outline' : (selectedDays.includes(index) ? 'mdi-checkbox-marked' :
                  'mdi-checkbox-blank-outline')) }}
              </v-icon>
            </div>
            <div class="d-flex justify-space-between text-caption grey--text px-1 mt-1"
              style="font-size: 10px !important;">
              <span v-for="day in days" :key="'reg-date-' + day.date"
                :class="{ 'greyed-out-text': day.isGreyedOut || (isMorningShift && !isWeekend(day)) }"
                style="width: 14%; text-align: center;">{{ day.date }}</span>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="mb-4">
            <div class="font-weight-medium text-body-2 mb-2">{{ activeTab === 0 ? 'Chọn ca làm việc' : 'Chọn thời gian'
              }}
            </div>
            <v-radio-group v-model="selectedShift" hide-details class="mt-0 pt-0">
              <v-radio v-if="activeTab === 0" value="sangIT" color="teal" :disabled="hasWeekdaySelected">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium"
                    :style="{ color: hasWeekdaySelected ? '#9E9E9E' : '#4CAF50' }">
                    Ca sáng (06:00 - 14:00)
                    <span class="text-caption font-weight-medium ml-1"
                      :class="hasWeekdaySelected ? 'grey--text' : 'green--text text--darken-2'">
                      (Chỉ T7, CN)
                    </span>
                  </span>
                </template>
              </v-radio>
              <v-radio v-if="activeTab === 0" value="chieuIT" color="teal" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium" style="color: #FF9800;">Ca Chiều (14:00 - 22:00)</span>
                </template>
              </v-radio>
              <v-radio v-if="activeTab === 0" value="wfhIT" color="teal darken-3" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium teal--text text--darken-3">WFH (07:30 - 17:30)</span>
                </template>
              </v-radio>
              <v-radio v-if="activeTab === 0" value="offIT" color="grey darken-2" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium grey--text text--darken-3">OFF IT (00:00 - 00:00)</span>
                </template>
              </v-radio>
              <!-- <v-radio value="gay" color="blue" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-bold" style="color: #2196F3;">
                    {{ activeTab === 0 ? 'Ca Gãy (Chọn thời gian)' : 'Chọn thời gian' }}
                  </span>
                </template>
              </v-radio> -->
            </v-radio-group>
          </div>

          <div v-if="selectedShift === 'gay'" class="mb-4 pl-8">
            <div class="text-caption font-weight-medium mb-2">
              {{ activeTab === 0 ? 'Nhập thời gian ca gãy:' : 'Nhập thời gian tăng ca:' }}
            </div>
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Từ</div>
                <v-text-field v-model="splitTime1From" dense outlined hide-details
                  class="rounded-lg text-body-2 bg-white"></v-text-field>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Đến</div>
                <v-text-field v-model="splitTime1To" dense outlined hide-details
                  class="rounded-lg text-body-2 bg-white"></v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Từ</div>
                <v-text-field v-model="splitTime2From" dense outlined hide-details class="rounded-lg text-body-2"
                  :disabled="activeTab === 1" :background-color="activeTab === 1 ? '#F3F4F6' : 'white'"></v-text-field>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Đến</div>
                <v-text-field v-model="splitTime2To" dense outlined hide-details class="rounded-lg text-body-2"
                  :disabled="activeTab === 1" :background-color="activeTab === 1 ? '#F3F4F6' : 'white'"></v-text-field>
              </v-col>
            </v-row>
          </div>

          <div class="mb-4 mt-auto pt-4">
            <div class="text-caption font-weight-bold mb-2">Ghi chú <span class="font-weight-regular grey--text">(Không
                bắt
                buộc)</span></div>
            <v-textarea outlined dense hide-details rows="3" placeholder="Nhập ghi chú nếu có..."
              class="rounded-lg text-body-2"></v-textarea>
          </div>

          <v-row dense>
            <v-col cols="6">
              <v-btn outlined block color="grey darken-1" class="rounded-lg font-weight-bold text-none"
                @click="resetForm">Hủy</v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn block color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text"
                :loading="loading" @click="saveRegistration">Lưu</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Test Chấm Công -->
    <v-dialog v-model="testCheckinDialog" max-width="500px">
      <v-card class="rounded-xl pa-4">
        <v-card-title class="headline font-weight-bold text-h6 px-2 pb-4">
          Test chấm công
        </v-card-title>
        <v-card-text class="px-2">
          <!-- Chọn nhân viên -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Chọn nhân viên:</span>
            <v-select v-model="testCheckinEmployeeId" :items="employees" item-text="name" item-value="id" outlined dense
              hide-details class="rounded-lg mt-1 font-weight-medium" background-color="white" label="Chọn nhân viên">
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="32" class="mr-2">
                    <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-body-2" style="line-height: 1.2;">{{ item.name }}</div>
                    <div class="text-caption grey--text" style="line-height: 1.2;">{{ item.id }}</div>
                  </div>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="32" class="mr-2">
                    <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-body-2">{{ item.name }}</div>
                    <div class="text-caption grey--text">{{ item.id }}</div>
                  </div>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Ngày chấm công -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Ngày chấm công:</span>
            <v-menu v-model="testCheckinDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckinDateText" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-calendar"></v-text-field>
              </template>
              <v-date-picker v-model="testCheckinDate" no-title scrollable color="teal darken-4"
                @change="testCheckinDateMenu = false"></v-date-picker>
            </v-menu>
          </div>

          <!-- Giờ checkin -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Giờ checkin:</span>
            <v-menu ref="checkinTimeMenu" v-model="testCheckinTimeMenu" :close-on-content-click="false"
              :nudge-right="40" transition="scale-transition" offset-y max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckinTime" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-clock-outline"></v-text-field>
              </template>
              <v-time-picker v-if="testCheckinTimeMenu" v-model="testCheckinTime" full-width format="24hr"
                color="teal darken-4" @click:minute="$refs.checkinTimeMenu.save(testCheckinTime)"></v-time-picker>
            </v-menu>
          </div>

          <!-- Giờ checkout -->
          <div class="mb-2">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Giờ checkout:</span>
            <v-menu ref="checkoutTimeMenu" v-model="testCheckoutTimeMenu" :close-on-content-click="false"
              :nudge-right="40" transition="scale-transition" offset-y max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckoutTime" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-clock-outline"></v-text-field>
              </template>
              <v-time-picker v-if="testCheckoutTimeMenu" v-model="testCheckoutTime" full-width format="24hr"
                color="teal darken-4" @click:minute="$refs.checkoutTimeMenu.save(testCheckoutTime)"></v-time-picker>
            </v-menu>
          </div>

          <!-- Kết quả chấm công -->
          <v-expand-transition>
            <div v-if="testCheckinResult" class="mt-4 pa-4 rounded-lg border-teal-light"
              style="background: linear-gradient(135deg, #E0F2F1 0%, #E8F5E9 100%); border: 1px solid #B2DFDB;">
              <div class="d-flex align-center mb-3">
                <v-icon color="teal darken-2" class="mr-2">mdi-check-circle-outline</v-icon>
                <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4">Kết quả tính công</span>
              </div>
              <v-row dense class="text-body-2">
                <v-col cols="6" class="grey--text text--darken-2 py-1">Mã nhân viên:</v-col>
                <v-col cols="6" class="font-weight-bold text-right py-1">{{ testCheckinResult.employeeCode ||
                  testCheckinResult.EmployeeCode || '—' }}</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Ngày làm việc:</v-col>
                <v-col cols="6" class="font-weight-bold text-right py-1">{{ formatResultDate(testCheckinResult.workDate
                  ||
                  testCheckinResult.WorkDate) }}</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Giờ công thực tế:</v-col>
                <v-col cols="6" class="font-weight-bold text-right green--text text--darken-2 py-1">{{
                  testCheckinResult.actualTime !== undefined ? testCheckinResult.actualTime :
                    (testCheckinResult.ActualTime
                      !== undefined ? testCheckinResult.ActualTime : 0) }} giờ</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Tăng ca hợp lệ:</v-col>
                <v-col cols="6" class="font-weight-bold text-right blue--text text--darken-2 py-1">{{
                  testCheckinResult.validOvetime !== undefined ? testCheckinResult.validOvetime :
                    (testCheckinResult.ValidOvetime !== undefined ? testCheckinResult.ValidOvetime :
                      (testCheckinResult.validOvertime !== undefined ? testCheckinResult.validOvertime : 0)) }} giờ</v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card-text>
        <v-card-actions class="px-2 pt-4">
          <v-btn outlined color="red darken-1" class="rounded-lg font-weight-bold text-none" :loading="loading"
            @click="resetEnroll">
            <v-icon left small>mdi-delete-outline</v-icon> Reset ngày
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined color="grey darken-1" class="rounded-lg font-weight-bold text-none mr-2"
            @click="closeTestCheckin">Hủy</v-btn>
          <v-btn color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text"
            @click="saveTestCheckin">Đồng ý</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
// Updated import to reference apiIt.js instead of api.js
import api from './apiIt.js';

// =========================================================================
// DANH SÁCH MÃ NHÂN VIÊN TRỰC PHÒNG IT (CẤU HÌNH CỐ ĐỊNH - DỄ DÀNG THAY ĐỔI TẠI ĐÂY)
// Để thêm / bớt nhân viên trực, bạn chỉ cần sửa hoặc thêm mã nhân viên vào mảng bên dưới:
// =========================================================================
export const ON_DUTY_EMPLOYEE_CODES = [
  'NS0560',
  'NS1253',
  'NS0246',
  'NS1329',
  // 'NS0531',
  'NS3270',
  // 'NS0188'
];

export default {
  name: 'WorkPlanIt', // Updated component name to reflect IT version
  data() {
    const selectedMonth = moment().date() >= 26
      ? moment().add(1, 'month').format('YYYY-MM')
      : moment().format('YYYY-MM');
    const currentWeekStart = moment().startOf('isoWeek');
    return {
      loading: false,
      defaultAvatar: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y',
      selectedBranch: '',
      branches: [],
      selectedShift: 'sangIT',
      dutyEmployeeCodes: ON_DUTY_EMPLOYEE_CODES, // Tham chiếu danh sách mã nhân viên trực cố định
      selectedEmployeeId: ON_DUTY_EMPLOYEE_CODES[0] || 'NS0560',
      onlyShowSelected: false, // Trạng thái ẩn/hiện nhân viên (chỉ xem NV được chọn)
      selectedDays: [],
      splitTime1From: '08:00',
      splitTime1To: '12:00',
      splitTime2From: '',
      splitTime2To: '',
      currentWeekStart,
      selectedMonth,
      monthMenu: false,
      activeTab: 0,
      testCheckinDialog: false,
      testCheckinEmployeeId: '',
      testCheckinDate: moment().format('YYYY-MM-DD'),
      testCheckinDateMenu: false,
      testCheckinTime: '08:00',
      testCheckinTimeMenu: false,
      testCheckoutTime: '17:00',
      testCheckoutTimeMenu: false,
      testCheckinResult: null,
      rotations: [],
      days: [],
      // Khởi tạo danh sách nhân viên trực ban đầu từ cấu hình cố định
      employees: ON_DUTY_EMPLOYEE_CODES.map(id => ({
        id,
        name: id,
        avatar: '',
        totalHours: 0,
        shifts: Array(7).fill(null)
      }))
    }
  },
  computed: {
    headerTitle() {
      return moment(this.selectedMonth, 'YYYY-MM').format('[Tháng] MM/YYYY');
    },
    selectedMonthText() {
      return moment(this.selectedMonth, 'YYYY-MM').format('[Tháng] MM/YYYY');
    },
    dateRangeText() {
      if (this.days.length === 0) return '';
      const start = this.days[0].date;
      const end = this.days[this.days.length - 1].date;
      const midDay = moment(this.currentWeekStart).add(3, 'days');
      const year = midDay.format('YYYY');
      return `${start} - ${end}/${year}`;
    },
    canPrevWeek() {
      const prevStart = moment(this.currentWeekStart).subtract(1, 'week');
      return this.isWeekInSelectedMonth(prevStart);
    },
    canNextWeek() {
      const nextStart = moment(this.currentWeekStart).add(1, 'week');
      return this.isWeekInSelectedMonth(nextStart);
    },
    employeesDebugJson() {
      return JSON.stringify(this.employees, null, 2);
    },
    testCheckinDateText() {
      return this.testCheckinDate ? moment(this.testCheckinDate).format('DD/MM/YYYY') : '';
    },
    // Danh sách nhân viên hiển thị (hỗ trợ chế độ lọc chỉ hiện nhân viên đang chọn)
    displayedEmployees() {
      if (this.onlyShowSelected && this.selectedEmployeeId) {
        const filtered = this.employees.filter(emp => emp.id === this.selectedEmployeeId);
        return filtered.length > 0 ? filtered : this.employees;
      }
      return this.employees;
    },
    // Tên nhân viên đang được chọn
    selectedEmployeeName() {
      const emp = this.employees.find(e => e.id === this.selectedEmployeeId);
      return emp ? emp.name : '';
    },
    // Kiểm tra ca làm việc hiện tại có phải ca sáng hay không
    isMorningShift() {
      return this.activeTab === 0 && (this.selectedShift === 'sangIT' || this.selectedShift === 'sang');
    },
    // Kiểm tra trong các ngày đang chọn có ngày nào là ngày trong tuần (T2 - T6) hay không
    hasWeekdaySelected() {
      return this.selectedDays.some(index => {
        const day = this.days[index];
        return day && !this.isWeekend(day);
      });
    }
  },
  watch: {
    // Tự động cuộn đến dòng nhân viên khi được chọn ở selector
    selectedEmployeeId: {
      handler(newId) {
        if (!newId) return;
        this.$nextTick(() => {
          const rowEl = document.getElementById(`emp-row-${newId}`);
          if (rowEl) {
            rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      }
    },
    selectedBranch() {
      this.loadData();
    },
    selectedShift(newVal) {
      if ((newVal === 'sangIT' || newVal === 'sang') && this.hasWeekdaySelected) {
        // Tự động bỏ chọn các ngày trong tuần nếu chuyển sang ca sáng
        this.selectedDays = this.selectedDays.filter(index => this.days[index] && this.isWeekend(this.days[index]));
      }
    },
    activeTab(val) {
      if (val === 1) {
        this.selectedShift = 'gay';
      } else {
        this.selectedShift = 'sangIT';
      }
      this.loadData();
    }
  },
  apiService: null,
  created() {
    // Instantiate the custom IT Api service
    this.apiService = new api();
    this.fetchBranches();
  },
  methods: {
    // Fetch departments list under K00001 (Support/IT)
    fetchBranches() {
      this.loading = true;
      this.apiService.getBranches()
        .then(res => {
          if (res.data && res.data.length > 0) {
            // Filter only the IT department with code value 'P00138'
            const itDepts = res.data.filter(obj => (obj.code || '').trim() === 'P00138');
            const targetDepts = itDepts.length > 0 ? itDepts : res.data;
            this.branches = targetDepts.map(obj => ({
              text: obj.name,
              value: obj.code
            }));
            this.selectedBranch = this.branches[0].value;
          }
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
          if (this.days.length === 0) {
            this.generateCalendar();
          }
        });
    },
    onAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },
    // Calendar days generation
    generateCalendar() {
      const daysList = [];
      const dayNames = ['Chủ Nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

      const targetMonth = moment(this.selectedMonth, 'YYYY-MM');
      const startPeriod = targetMonth.clone().subtract(1, 'month').date(26).startOf('day');
      const endPeriod = targetMonth.clone().date(25).endOf('day');

      for (let i = 0; i < 7; i++) {
        const day = moment(this.currentWeekStart).add(i, 'days');
        daysList.push({
          name: dayNames[day.day()],
          date: day.format('DD/MM'),
          fullDate: day.format('YYYY-MM-DD'),
          isGreyedOut: !(day.isSameOrAfter(startPeriod) && day.isSameOrBefore(endPeriod))
        });
      }
      this.days = daysList;
      this.selectedDays = [];
      this.loadData();
    },
    // Fetch workplan schedules and employee list
    loadData() {
      if (!this.days || this.days.length === 0) return;

      const storeCode = this.selectedBranch ? this.selectedBranch.split(' ')[0] : '';
      const fromDate = this.days[0].fullDate;
      const toDate = this.days[this.days.length - 1].fullDate;

      this.loading = true;
      this.apiService.getEmployeesByDept("P00138")
        .then(resEmp => {
          const empList = resEmp.data.data || resEmp.data || [];
          let mappedEmployees = [];

          // Lọc theo danh sách nhân viên trực cấu hình cố định (ON_DUTY_EMPLOYEE_CODES)
          if (ON_DUTY_EMPLOYEE_CODES && ON_DUTY_EMPLOYEE_CODES.length > 0) {
            mappedEmployees = ON_DUTY_EMPLOYEE_CODES.map(code => {
              const found = empList.find(emp => (emp.code || emp.employeeCode || emp.id || '').trim() === code);
              return {
                id: code,
                name: found ? (found.name || found.fullName || found.employeeName || code) : code,
                avatar: found ? (found.avatar || '') : '',
                totalHours: 0,
                shifts: Array(7).fill(null)
              };
            });
          } else {
            mappedEmployees = empList.map(emp => ({
              id: emp.code || emp.employeeCode || emp.id || '',
              name: emp.name || emp.fullName || emp.employeeName || '',
              avatar: emp.avatar || '',
              totalHours: 0,
              shifts: Array(7).fill(null)
            }));
          }

          const typeParam = this.activeTab === 0 ? 'shift' : 'ot';
          const apiCall = this.activeTab === 0
            ? this.apiService.getWorkPlans(storeCode, fromDate, toDate, typeParam)
            : this.apiService.getOvertimePlans(storeCode, fromDate, toDate, typeParam);

          const prevMonth = moment(this.selectedMonth, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM');
          const rotationCall = this.apiService.getEmployeeRotations(this.selectedMonth + '-01')
            .catch(err => {
              console.error('Error fetching rotations:', err);
              return { data: [] };
            });
          const prevRotationCall = this.apiService.getEmployeeRotations(prevMonth + '-01')
            .catch(err => {
              console.error('Error fetching previous rotations:', err);
              return { data: [] };
            });

          return Promise.all([apiCall, rotationCall, prevRotationCall])
            .then(([resPlans, resRotations, resPrevRotations]) => {
              const plansList = resPlans.data.data || resPlans.data || [];
              const rotationsCurr = resRotations.data.data || resRotations.data || [];
              const rotationsPrev = resPrevRotations.data.data || resPrevRotations.data || [];
              this.rotations = [...rotationsCurr, ...rotationsPrev];
              if (Array.isArray(plansList)) {
                mappedEmployees.forEach(emp => {
                  const plan = plansList.find(p => {
                    const pCode = p.employeeCode || p.EmployeeCode || '';
                    return pCode === emp.id;
                  });

                  if (plan) {
                    const rawWorkPlans = this.activeTab === 0
                      ? (plan.workPlans || plan.WorkPlans || [])
                      : (plan.overtimePlans || plan.OvertimePlans || plan.workPlans || plan.WorkPlans || []);
                    const mappedShifts = rawWorkPlans.map(wp => {
                      if (!wp) return null;
                      const type = wp.type || wp.Type || '';
                      const title = wp.title || wp.Title || '';
                      const time1 = wp.time1 || wp.Time1 || '';
                      const time2 = wp.time2 || wp.Time2 || '';
                      return { type, title, time1, time2 };
                    });

                    while (mappedShifts.length < 7) {
                      mappedShifts.push(null);
                    }

                    emp.shifts = mappedShifts;
                    emp.totalHours = plan.totalHours !== undefined ? plan.totalHours : (plan.TotalHours || 0);
                  }
                });
              }
              this.employees = mappedEmployees;
              // Đảm bảo selectedEmployeeId luôn là nhân viên hợp lệ trong danh sách trực
              if (this.employees.length > 0 && !this.employees.some(e => e.id === this.selectedEmployeeId)) {
                this.selectedEmployeeId = this.employees[0].id;
              }
              this.employees.forEach(emp => this.calculateTotalHours(emp));
            });
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    prevWeek() {
      if (!this.canPrevWeek) return;
      this.currentWeekStart = moment(this.currentWeekStart).subtract(1, 'week');
      this.generateCalendar();
    },
    nextWeek() {
      if (!this.canNextWeek) return;
      this.currentWeekStart = moment(this.currentWeekStart).add(1, 'week');
      this.generateCalendar();
    },
    isWeekInSelectedMonth(weekStart) {
      const start = moment(weekStart);
      const end = moment(weekStart).add(6, 'days');
      const targetMonth = moment(this.selectedMonth, 'YYYY-MM');
      const startPeriod = targetMonth.clone().subtract(1, 'month').date(26).startOf('day');
      const endPeriod = targetMonth.clone().date(25).endOf('day');
      return !(end.isBefore(startPeriod) || start.isAfter(endPeriod));
    },
    onMonthChange(val) {
      this.monthMenu = false;
      this.currentWeekStart = moment(val, 'YYYY-MM').subtract(1, 'month').date(26).startOf('isoWeek');
      this.generateCalendar();
    },
    // Kiểm tra ngày có phải Thứ 7 hoặc Chủ Nhật hay không
    isWeekend(day) {
      if (!day) return false;
      if (day.name === 'Thứ 7' || day.name === 'Chủ Nhật') return true;
      if (day.fullDate) {
        const d = moment(day.fullDate).day();
        return d === 0 || d === 6;
      }
      return false;
    },
    toggleRegDay(index) {
      const day = this.days[index];
      if (!day || day.isGreyedOut) return;
      // Ca sáng chỉ cho phép chọn vào Thứ 7 hoặc Chủ Nhật
      if (this.isMorningShift && !this.isWeekend(day)) {
        alert('Ca sáng chỉ áp dụng cho Thứ 7 hoặc Chủ Nhật.');
        return;
      }
      const pos = this.selectedDays.indexOf(index);
      if (pos > -1) {
        this.selectedDays.splice(pos, 1);
      } else {
        this.selectedDays.push(index);
      }
    },
    calculateTotalHours(employee) {
      let total = 0;
      employee.shifts.forEach(shift => {
        if (!shift || shift.type === 'nghi' || shift.type === 'offIT' || shift.type === 'off') return;
        if (shift.type === 'sangIT' || shift.type === 'chieuIT' || shift.type === 'sang' || shift.type === 'chieu' || shift.type === 'wfhIT') {
          total += 8;
        } else if (shift.type === 'gay') {
          total += 8;
        }
      });
      employee.totalHours = total;
    },
    // Xử lý khi click vào ô trong bảng data: tự động chọn nhân viên, ca chiều và thứ tương ứng
    onCellClick(emp, index) {
      if (!emp || !this.days[index] || this.days[index].isGreyedOut) return;

      // 1. Chọn nhân viên đăng ký
      this.selectedEmployeeId = emp.id;

      // 2. Ca mặc định là ca chiều
      if (this.activeTab === 0) {
        this.selectedShift = 'chieuIT';
      } else {
        this.selectedShift = 'gay';
      }

      // 3. Thứ được chọn sẵn từ vị trí cột được click
      this.selectedDays = [index];
    },
    // Deletes an assigned shift
    deleteShift(emp, index) {
      const day = this.days[index];
      if (!day) return;

      const dateStr = day.date;
      const confirmMsg = `Bạn có chắc chắn muốn xóa lịch làm việc ngày ${dateStr} của nhân viên ${emp.name}?`;
      if (!confirm(confirmMsg)) return;

      const employeeCode = emp.id;
      const workDate = day.fullDate;
      const type = this.activeTab === 0 ? 'shift' : 'ot';

      this.loading = true;
      this.apiService.deleteWorkPlan(employeeCode, workDate, type)
        .then(() => {
          this.$set(emp.shifts, index, null);
          this.calculateTotalHours(emp);
        })
        .catch(err => {
          console.error(err);
          alert('Có lỗi xảy ra khi xóa lịch làm việc.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Registers/saves new work plan or OT shift
    saveRegistration() {
      const employee = this.employees.find(emp => emp.id === this.selectedEmployeeId);
      if (!employee) return;

      if (!this.selectedDays || this.selectedDays.length === 0) {
        alert('Vui lòng chọn ít nhất một ngày để đăng ký ca.');
        return;
      }

      // Kiểm tra nếu chọn ca sáng thì chỉ được phép đăng ký vào Thứ 7 hoặc Chủ Nhật
      if (this.isMorningShift && this.hasWeekdaySelected) {
        alert('Ca sáng chỉ áp dụng cho Thứ 7 hoặc Chủ Nhật. Vui lòng kiểm tra lại các ngày đã chọn.');
        return;
      }

      const dates = this.selectedDays
        .filter(index => this.days[index] && !this.days[index].isGreyedOut)
        .map(index => this.days[index].fullDate);

      if (dates.length === 0) {
        alert('Các ngày đã chọn không hợp lệ hoặc nằm ngoài chu kỳ sắp ca.');
        return;
      }

      const baseDate = dates[0];
      let startHour1 = null;
      let endHour1 = null;
      let startHour2 = null;
      let endHour2 = null;

      if (this.selectedShift === 'sangIT' || this.selectedShift === 'sang') {
        startHour1 = `${baseDate}T06:00:00`;
        endHour1 = `${baseDate}T14:00:00`;
      } else if (this.selectedShift === 'chieuIT' || this.selectedShift === 'chieu') {
        startHour1 = `${baseDate}T14:00:00`;
        endHour1 = `${baseDate}T22:00:00`;
      } else if (this.selectedShift === 'wfhIT') {
        // Cố định giờ làm việc WFH IT: 07:30 - 17:30
        startHour1 = `${baseDate}T07:30:00`;
        endHour1 = `${baseDate}T17:30:00`;
      } else if (this.selectedShift === 'offIT') {
        // Cố định giờ nghỉ OFF IT: 00:00 - 00:00
        startHour1 = `${baseDate}T00:00:00`;
        endHour1 = `${baseDate}T00:00:00`;
      } else if (this.selectedShift === 'gay') {
        startHour1 = `${baseDate}T${this.splitTime1From}:00`;
        endHour1 = `${baseDate}T${this.splitTime1To}:00`;
        if (this.activeTab === 0) {
          startHour2 = `${baseDate}T${this.splitTime2From}:00`;
          endHour2 = `${baseDate}T${this.splitTime2To}:00`;
        } else {
          startHour2 = null;
          endHour2 = null;
        }
      }

      const payload = {
        EmployeeCode: this.selectedEmployeeId,
        Dates: dates,
        ShiftSymbol: this.selectedShift,
        StartHour1: startHour1,
        EndHour1: endHour1,
        StartHour2: startHour2,
        EndHour2: endHour2,
        Type: this.activeTab === 0 ? 'shift' : 'ot'
      };

      let shiftObj = null;
      if (this.selectedShift === 'sangIT' || this.selectedShift === 'sang') {
        shiftObj = { type: 'sangIT', title: 'CA SÁNG', time1: '06:00 - 14:00' };
      } else if (this.selectedShift === 'chieuIT' || this.selectedShift === 'chieu') {
        shiftObj = { type: 'chieuIT', title: 'CA CHIỀU', time1: '14:00 - 22:00' };
      } else if (this.selectedShift === 'wfhIT') {
        shiftObj = { type: 'wfhIT', title: 'WFH', time1: '07:30 - 17:30' };
      } else if (this.selectedShift === 'offIT') {
        shiftObj = { type: 'offIT', title: 'OFF', time1: '00:00 - 00:00' };
      } else if (this.selectedShift === 'gay') {
        shiftObj = {
          type: this.activeTab === 0 ? 'gay' : 'ot',
          title: this.activeTab === 0 ? 'CA GÃY' : 'TĂNG CA',
          time1: `${this.splitTime1From} - ${this.splitTime1To}`,
          time2: this.activeTab === 0 && this.splitTime2From && this.splitTime2To ? `${this.splitTime2From} - ${this.splitTime2To}` : null
        };
      } else if (this.selectedShift === 'nghi') {
        shiftObj = { type: 'nghi', title: 'NGHỈ' };
      }

      // Lưu lại danh sách ngày đã chọn để cập nhật giao diện sau khi API phản hồi thành công
      const daysToUpdate = [...this.selectedDays];

      this.loading = true;
      const apiCall = this.activeTab === 0
        ? this.apiService.createWorkPlan(payload)
        : this.apiService.createOvertimePlan(payload);

      apiCall
        .then(res => {
          daysToUpdate.forEach(index => {
            if (this.days[index] && this.days[index].isGreyedOut) return;
            this.$set(employee.shifts, index, shiftObj);
          });
          this.calculateTotalHours(employee);
          // Reset các option thứ đã chọn về rỗng sau mỗi lần lưu
          this.selectedDays = [];
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatResultDate(val) {
      if (!val) return '';
      return moment(val).format('DD/MM/YYYY');
    },
    openTestCheckin() {
      this.testCheckinResult = null;
      this.testCheckinEmployeeId = this.selectedEmployeeId || (this.employees && this.employees.length > 0 ? this.employees[0].id : '');
      this.testCheckinDialog = true;
    },
    saveTestCheckin() {
      if (!this.testCheckinEmployeeId || !this.testCheckinDate || !this.testCheckinTime || !this.testCheckoutTime) {
        alert('Vui lòng điền đầy đủ thông tin.');
        return;
      }
      const employeeCode = this.testCheckinEmployeeId;
      const workDate = this.testCheckinDate;
      const checkin = this.testCheckinTime.length === 5 ? `${this.testCheckinTime}:00` : this.testCheckinTime;
      const checkout = this.testCheckoutTime.length === 5 ? `${this.testCheckoutTime}:00` : this.testCheckoutTime;
      const type = this.activeTab === 0 ? 'shift' : 'ot';

      this.loading = true;
      this.apiService.enroll(employeeCode, workDate, checkin, checkout, type)
        .then((res) => {
          this.testCheckinResult = res.data.data || res.data || null;
          this.loadData();
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetEnroll() {
      if (!this.testCheckinEmployeeId || !this.testCheckinDate) {
        alert('Vui lòng chọn nhân viên và ngày cần reset.');
        return;
      }

      const employee = this.employees.find(emp => emp.id === this.testCheckinEmployeeId);
      const empName = employee ? employee.name : this.testCheckinEmployeeId;
      const dateText = this.testCheckinDateText;

      if (!confirm(`Bạn có chắc chắn muốn reset công ngày ${dateText} của nhân viên ${empName}?`)) {
        return;
      }

      const employeeCode = this.testCheckinEmployeeId;
      const workdate = this.testCheckinDate;

      this.loading = true;
      this.apiService.resetEnroll(employeeCode, workdate)
        .then(() => {
          this.testCheckinResult = null;
          this.loadData();
          alert('Đã reset ngày chấm công thành công.');
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeTestCheckin() {
      this.testCheckinDialog = false;
      this.testCheckinEmployeeId = '';
      this.testCheckinDate = moment().format('YYYY-MM-DD');
      this.testCheckinTime = '08:00';
      this.testCheckoutTime = '17:00';
      this.testCheckoutTimeMenu = false;
      this.testCheckinResult = null;
    },
    resetForm() {
      this.selectedShift = 'sangIT';
      this.selectedDays = [];
      this.splitTime1From = '08:00';
      this.splitTime1To = '12:00';
      this.splitTime2From = '';
      this.splitTime2To = '';
    },
    // Chuyển đổi trạng thái ẩn/hiện nhân viên (chỉ xem NV được chọn hoặc xem tất cả)
    toggleShowSelected() {
      this.onlyShowSelected = !this.onlyShowSelected;
    },
    getRotationDetail(empId, fullDate) {
      if (!this.rotations || !Array.isArray(this.rotations)) return null;
      return this.rotations.find(r => {
        const rCode = r.employeeCode || r.EmployeeCode || '';
        if (rCode !== empId) return false;

        const rDateRaw = r.rotationDateFrom
        if (!rDateRaw) return false;

        return moment(rDateRaw).format('YYYY-MM-DD') === fullDate;
      });
    },
    formatRotationTooltipDate(rotation) {
      if (!rotation) return '';
      const rDate = rotation.rotationDateFrom || rotation.rotationDate || rotation.RotationDate || rotation.effectiveDate || rotation.EffectiveDate || rotation.date || rotation.Date;
      return moment(rDate).format('DD/MM/YYYY');
    }
  }
}
</script>

<style scoped>
.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 6px;
}

.bg-green {
  background-color: #4CAF50;
}

.bg-teal-wfh {
  background-color: #00897B;
}

.bg-orange {
  background-color: #FF9800;
}

.bg-blue {
  background-color: #2196F3;
}

.bg-grey {
  background-color: #E0E0E0;
}

.schedule-table th {
  background-color: #FAFAFA !important;
  border-bottom: 1px solid #EEEEEE !important;
  color: #333333 !important;
}

.schedule-table td {
  border-bottom: 1px solid #F5F5F5 !important;
  border-right: 1px solid #F5F5F5 !important;
}

/* Custom scrollbar for fixed height table */
.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #F3F4F6;
  border-radius: 4px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}

.shift-block {
  border-radius: 8px;
  padding: 6px 2px;
  font-size: 11px;
  line-height: 1.2;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.shift-block.sang,
.shift-block.sangIT {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.shift-block.wfh,
.shift-block.wfhIT {
  background-color: #E0F2F1;
  color: #00695C;
  border: 1px solid #B2DFDB;
}

.shift-block.chieu,
.shift-block.chieuIT {
  background-color: #FFF3E0;
  color: #E65100;
}

.shift-block.gay,
.shift-block.ot {
  background-color: #E3F2FD;
  color: #1565C0;
}

.shift-block.nghi,
.shift-block.off,
.shift-block.offIT {
  background-color: #F5F5F5;
  color: #616161;
}

.shift-time {
  margin-top: 2px;
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline;
}

.avatar-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.avatar-clickable:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Greyed out calendar elements for days in previous month */
.greyed-out-header {
  background-color: #EEEEEE !important;
  color: #BDBDBD !important;
  opacity: 0.7;
}

.greyed-out-cell {
  background-color: #F9FAFB !important;
  color: #E0E0E0 !important;
}

.greyed-out-placeholder {
  color: #E0E0E0;
  font-weight: 500;
  font-size: 14px;
}

.greyed-out-text {
  color: #BDBDBD !important;
}

.cursor-not-allowed {
  cursor: not-allowed !important;
}

/* Highlight dòng nhân viên được chọn màu cam */
.schedule-table tr.highlight-selected-emp {
  background-color: #FFF3E0 !important;
  transition: all 0.25s ease;
}

.schedule-table tr.highlight-selected-emp td {
  background-color: #FFF3E0 !important;
}

.schedule-table tr.highlight-selected-emp td.employee-cell {
  background-color: #FFE0B2 !important;
  border-left: 4px solid #FB8C00 !important;
}

.schedule-table tbody tr.highlight-selected-emp:hover td {
  background-color: #FFE8CC !important;
}

.schedule-table tbody tr.highlight-selected-emp:hover td.employee-cell {
  background-color: #FFD699 !important;
}

/* Avatar khi được chọn có viền cam nổi bật */
.avatar-selected {
  outline: 2px solid #FB8C00 !important;
  outline-offset: 2px;
}

/* Cell được chọn trong bảng */
.schedule-table td.cell-selected {
  outline: 2px solid #FB8C00 !important;
  outline-offset: -2px;
  background-color: #FFE0B2 !important;
}

.empty-shift-cell {
  min-height: 48px;
  width: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.schedule-table td.cursor-pointer:hover .empty-shift-cell {
  background-color: rgba(251, 140, 0, 0.08);
}

.shift-delete-btn {
  position: absolute !important;
  top: 2px;
  right: 2px;
  width: 18px !important;
  height: 18px !important;
  opacity: 0.6;
  transition: opacity 0.2s;
  z-index: 1;
}

.shift-block:hover .shift-delete-btn {
  opacity: 1;
}

.shift-delete-btn:hover {
  background-color: rgba(0, 0, 0, 0.12) !important;
}

.position-relative {
  position: relative !important;
}
</style>
